import React, { useEffect, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { MapPin, Plane, ArrowRight, ShieldCheck } from "lucide-react";
import { BRAND, SEO_BASE } from "../config/brand";
import { CITIES, buildPage, cityName } from "../data/seoData";

const LANGS4 = ["de", "en", "fr", "it"];

const CT = {
  de: { kicker: (k) => `Kanton ${k} · Fixpreis`, title: (k) => `Flughafentransfer Zürich – Kanton ${k}`, intro: (n, k) => `Wir bringen Sie zum garantierten Fixpreis vom Flughafen Zürich (ZRH) in alle ${n} Gemeinden im Kanton ${k}. Wählen Sie Ihre Gemeinde für Distanz, Fixpreis und Online-Buchung – 24/7, mit Flugverfolgung.`, townsT: (k) => `Gemeinden im Kanton ${k}`, facts: ["Gemeinden", "Nächste", "Weiteste", "Verfügbar"], back: "← Alle Kantone", cta: "Preis berechnen" },
  en: { kicker: (k) => `Canton ${k} · Fixed price`, title: (k) => `Zurich Airport Transfer – Canton ${k}`, intro: (n, k) => `We drive you at a guaranteed fixed price from Zurich Airport (ZRH) to all ${n} municipalities in the canton of ${k}. Pick your town for distance, fixed price and online booking – 24/7 with flight tracking.`, townsT: (k) => `Municipalities in canton ${k}`, facts: ["Municipalities", "Nearest", "Farthest", "Available"], back: "← All cantons", cta: "Calculate price" },
  fr: { kicker: (k) => `Canton ${k} · Prix fixe`, title: (k) => `Transfert aéroport de Zurich – Canton ${k}`, intro: (n, k) => `Nous vous conduisons à prix fixe garanti depuis l'aéroport de Zurich (ZRH) vers les ${n} communes du canton de ${k}. Choisissez votre commune pour la distance, le prix fixe et la réservation en ligne – 24h/24 avec suivi des vols.`, townsT: (k) => `Communes du canton ${k}`, facts: ["Communes", "La plus proche", "La plus éloignée", "Disponible"], back: "← Tous les cantons", cta: "Calculer le prix" },
  it: { kicker: (k) => `Cantone ${k} · Prezzo fisso`, title: (k) => `Transfer aeroporto di Zurigo – Cantone ${k}`, intro: (n, k) => `Ti portiamo a prezzo fisso garantito dall'aeroporto di Zurigo (ZRH) verso tutti i ${n} comuni del cantone di ${k}. Scegli il tuo comune per distanza, prezzo fisso e prenotazione online – 24/7 con monitoraggio voli.`, townsT: (k) => `Comuni del cantone ${k}`, facts: ["Comuni", "Più vicino", "Più lontano", "Disponibile"], back: "← Tutti i cantoni", cta: "Calcola il prezzo" },
};

const kSlug = (k) => k.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const upsertLink = (rel, href, hreflang) => {
  const sel = hreflang ? `link[rel="alternate"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); if (hreflang) el.setAttribute("hreflang", hreflang); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

// Build canton lookup once.
const CANTON_MAP = {};
CITIES.forEach((c) => { (CANTON_MAP[c.k] = CANTON_MAP[c.k] || []).push(c); });
const SLUG_TO_CANTON = {};
Object.keys(CANTON_MAP).forEach((k) => { SLUG_TO_CANTON[kSlug(k)] = k; });

const CantonLanding = () => {
  const path = window.location.pathname.split("/").filter(Boolean); // [lang, "kanton", slug]
  const lang = LANGS4.includes(path[0]) ? path[0] : "de";
  const cantonSlug = path[2] || "";
  const canton = SLUG_TO_CANTON[cantonSlug] || null;

  const towns = useMemo(() => (canton ? [...CANTON_MAP[canton]].sort((a, b) => a.km - b.km) : []), [canton]);
  const t = CT[lang];

  useEffect(() => {
    if (!canton) return;
    const url = `${SEO_BASE}/${lang}/kanton/${cantonSlug}`;
    document.title = `${t.title(canton)} | ${BRAND.name}`;
    upsertMeta("name", "description", t.intro(towns.length, canton).slice(0, 300));
    upsertLink("canonical", url);
    upsertMeta("property", "og:title", t.title(canton));
    upsertMeta("property", "og:url", url);
    LANGS4.forEach((l) => upsertLink("alternate", `${SEO_BASE}/${l}/kanton/${cantonSlug}`, l));
    upsertLink("alternate", `${SEO_BASE}/de/kanton/${cantonSlug}`, "x-default");
    const ld = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: t.title(canton),
      areaServed: { "@type": "AdministrativeArea", name: `Kanton ${canton}` },
      provider: { "@type": "LocalBusiness", name: BRAND.name, telephone: BRAND.phone, url: SEO_BASE },
    };
    let s = document.getElementById("canton-ld"); if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = "canton-ld"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
    window.scrollTo(0, 0);
    return () => { const el = document.getElementById("canton-ld"); if (el) el.textContent = "{}"; };
  }, [canton, lang, cantonSlug, towns.length, t]);

  if (!canton) return <Navigate to="/" replace />;

  const facts = [
    { i: MapPin, l: t.facts[0], v: towns.length },
    { i: Plane, l: t.facts[1], v: `${towns[0].km} km` },
    { i: Plane, l: t.facts[2], v: `${towns[towns.length - 1].km} km` },
    { i: ShieldCheck, l: t.facts[3], v: "24/7" },
  ];

  return (
    <div className="bg-white" data-testid="canton-landing">
      <section className="relative pt-28 pb-14 bg-[#0B1533] overflow-hidden">
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6">
          <p className="font-body text-xs uppercase tracking-[0.28em] text-[#C9A24B] mb-3">{t.kicker(canton)}</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight" data-testid="canton-h1">{t.title(canton)}</h1>
          <p className="font-body text-white/75 text-sm sm:text-base mt-4 max-w-2xl leading-relaxed">{t.intro(towns.length, canton)}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-8 relative z-20">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {facts.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#E9E5DE] p-4 elev-soft text-center">
              <f.i size={20} className="text-[#2B4BFF] mx-auto mb-2" />
              <p className="font-body text-xs text-[#5A607A]">{f.l}</p>
              <p className="font-heading font-bold text-[#0B1533]">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="font-heading font-bold text-2xl text-[#0B1533] mb-5">{t.townsT(canton)}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="canton-towns">
          {towns.map((c) => {
            const pg = buildPage(lang, "transfer", c);
            return (
              <Link key={c.s} to={`/${lang}/${pg.slug}`} data-testid={`canton-town-${c.s}`} className="group flex items-center gap-2 rounded-xl border border-[#E9E5DE] bg-white px-3 py-2.5 hover:border-[#2B4BFF] hover:elev-soft transition-all">
                <Plane size={14} className="text-[#2B4BFF] shrink-0" />
                <span className="font-heading font-semibold text-sm text-[#0B1533] truncate flex-1">{cityName(c, lang)}</span>
                <span className="font-body text-xs text-[#9CA0B0] shrink-0">{c.km} km</span>
                <ArrowRight size={14} className="text-[#2B4BFF] opacity-0 group-hover:opacity-100 transition-all shrink-0" />
              </Link>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link to="/ziele-alle" className="font-body text-sm text-[#2B4BFF] hover:underline">{t.back}</Link>
        </div>
      </section>
    </div>
  );
};

export default CantonLanding;
